import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Minimize2,
  Download, Printer, RotateCw, FileText, Loader2,
} from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return url;
};

/* ─── Premium PDF Viewer ───────────────────────────────────────────── */
const PdfViewer = ({
  url,
  label = 'Document',
  accentColor = '#00457f',
  showDownload = true,
  showToolbar = true,
  initialHeight = 640,
  compact = false,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const docUrl = resolveUrl(url);

  // Measure container for responsive page width
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setFullscreen(false);
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') setCurrentPage(p => Math.min(numPages || 1, p + 1));
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') setCurrentPage(p => Math.max(1, p - 1));
      if (e.key === '+' || e.key === '=') setScale(s => Math.min(3, s + 0.15));
      if (e.key === '-') setScale(s => Math.max(0.5, s - 0.15));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen, numPages]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback(() => {
    setLoadError(true);
    setLoading(false);
  }, []);

  const goToPrev = () => setCurrentPage(p => Math.max(1, p - 1));
  const goToNext = () => setCurrentPage(p => Math.min(numPages || 1, p + 1));
  const zoomIn = () => setScale(s => Math.min(3, s + 0.2));
  const zoomOut = () => setScale(s => Math.max(0.5, s - 0.2));
  const rotate = () => setRotation(r => (r + 90) % 360);
  const handlePrint = () => { const w = window.open(docUrl); if (w) w.print(); };

  const pageWidth = Math.max(300, (containerWidth - 48) / scale);

  // Error / fallback state
  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="pdf-viewer-error">
        <FileText className="w-16 h-16 mb-4 text-slate-300" />
        <p className="text-sm font-medium">Unable to render PDF preview</p>
        {showDownload && (
          <a
            href={docUrl}
            download={label}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold transition-transform hover:scale-105"
            style={{ backgroundColor: accentColor }}
          >
            <Download className="w-4 h-4" /> Download Instead
          </a>
        )}
      </div>
    );
  }

  const viewerContent = (
    <div
      ref={containerRef}
      className={`flex flex-col ${fullscreen ? 'fixed inset-0 z-[10002] bg-[#1a1a2e]' : 'relative'}`}
      data-testid="pdf-viewer"
    >
      {/* ── Toolbar ── */}
      {showToolbar && (
        <div
          className={`flex items-center justify-between gap-2 px-4 py-2.5 ${
            fullscreen
              ? 'bg-[#0f0f23]/90 backdrop-blur-xl border-b border-white/5 text-white'
              : 'bg-white/80 backdrop-blur-md border-b border-slate-100 text-slate-700'
          }`}
          data-testid="pdf-toolbar"
        >
          {/* Left: page nav */}
          <div className="flex items-center gap-1">
            <button
              onClick={goToPrev}
              disabled={currentPage <= 1}
              className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
              data-testid="pdf-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-mono ${fullscreen ? 'bg-white/5' : 'bg-slate-50'}`}>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (v >= 1 && v <= (numPages || 1)) setCurrentPage(v);
                }}
                className="w-8 text-center bg-transparent outline-none tabular-nums"
                min={1}
                max={numPages || 1}
                data-testid="pdf-page-input"
              />
              <span className="opacity-50">/</span>
              <span className="tabular-nums">{numPages || '—'}</span>
            </div>
            <button
              onClick={goToNext}
              disabled={currentPage >= (numPages || 1)}
              className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
              data-testid="pdf-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Center: zoom + rotate */}
          <div className="flex items-center gap-1">
            <button onClick={zoomOut} className={`p-1.5 rounded-lg transition-colors ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`} data-testid="pdf-zoom-out">
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className={`text-xs font-mono px-2 py-1 rounded-lg min-w-[48px] text-center ${fullscreen ? 'bg-white/5' : 'bg-slate-50'}`}>
              {Math.round(scale * 100)}%
            </span>
            <button onClick={zoomIn} className={`p-1.5 rounded-lg transition-colors ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`} data-testid="pdf-zoom-in">
              <ZoomIn className="w-4 h-4" />
            </button>
            {!compact && (
              <button onClick={rotate} className={`p-1.5 rounded-lg transition-colors ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`} data-testid="pdf-rotate">
                <RotateCw className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1">
            {!compact && (
              <button onClick={handlePrint} className={`p-1.5 rounded-lg transition-colors ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`} title="Print" data-testid="pdf-print">
                <Printer className="w-4 h-4" />
              </button>
            )}
            {showDownload && (
              <a href={docUrl} download={label} target="_blank" rel="noopener noreferrer" className={`p-1.5 rounded-lg transition-colors ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`} title="Download" data-testid="pdf-download">
                <Download className="w-4 h-4" />
              </a>
            )}
            <button
              onClick={() => setFullscreen(f => !f)}
              className={`p-1.5 rounded-lg transition-colors ${fullscreen ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
              title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              data-testid="pdf-fullscreen"
            >
              {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      {/* ── Page rendering area ── */}
      <div
        className={`flex-1 overflow-auto flex justify-center ${
          fullscreen ? 'bg-[#1a1a2e]' : 'bg-gradient-to-b from-slate-100 to-slate-50'
        }`}
        style={{ height: fullscreen ? undefined : initialHeight, minHeight: compact ? 300 : 400 }}
        data-testid="pdf-page-area"
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
              <p className="text-xs text-slate-400 font-medium">Loading document...</p>
            </div>
          </div>
        )}
        <div className={`py-6 transition-opacity duration-500 ${loading ? 'opacity-0' : 'opacity-100'}`}>
          <Document
            file={docUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading=""
          >
            <div
              className="shadow-2xl rounded-sm mx-auto"
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease',
              }}
            >
              <Page
                pageNumber={currentPage}
                width={pageWidth}
                rotate={rotation}
                renderTextLayer
                renderAnnotationLayer
                loading=""
              />
            </div>
          </Document>
        </div>
      </div>

      {/* ── Bottom page indicator (compact) ── */}
      {numPages > 1 && !showToolbar && (
        <div className="flex items-center justify-center gap-3 py-2 bg-white/80 backdrop-blur-sm border-t border-slate-100 text-xs text-slate-500">
          <button onClick={goToPrev} disabled={currentPage <= 1} className="disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-mono">{currentPage} / {numPages}</span>
          <button onClick={goToNext} disabled={currentPage >= numPages} className="disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );

  return viewerContent;
};

export { PdfViewer, resolveUrl };
export default PdfViewer;
